import { useMemo, useState, type KeyboardEvent } from 'react';
import {
  AlertTriangle,
  Building2,
  Check,
  ChevronsUpDown,
  CloudUpload,
  Loader2,
  PackageCheck,
  Plus,
  Search,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { useSalesOrderStore } from '../store/salesOrderStore';
import { AddressPreview } from './AddressPreview';
import { AddressCard } from './AddressCard';
import { AddNewAddressDialog } from './AddNewAddressDialog';
import { useReferenceName } from './ReferenceDataCombobox';
import {
  PROVINCE_YN,
  PROVINCE_YN_LABEL,
  type AddressDto,
  type AppendAddressRequest,
  type ShippingAddressInput,
} from '../types/salesOrder.types';

interface Props {
  value: ShippingAddressInput;
  onChange: (next: ShippingAddressInput) => void;
  readOnly?: boolean;
  /** Whose address book to read and write. */
  customerId: string | null;
  customerName: string;
  /** Order context — seeds the dialog and its Province suggestion. */
  stateProvinceId: string | null;
  cityTownId: string | null;
}

const ADD_NEW = '__add_new__';
const PICKUP = '__pickup__';

// 86d405z28 — the shipping-address control.
//
// PICK-UP is NOT a synthetic option any more. It used to fabricate an inline address block
// (addressee "PICK-UP") which NetSuite rejected outright for the mandatory PROVINCE (Y/N)
// field, so *no pick-up order could be posted at all*. NetSuite's own convention is a real
// saved address record labelled PICK-UP — 763 customers already have one — so the option now
// resolves to that record and offers to create it once when it is missing.
//
// Which record counts as the pick-up one is decided SERVER-SIDE (`isPickupCandidate`); this
// component only counts them. See CustomerAddressBookService for why the label, not the
// address text, is the discriminator.
type SaveState =
  | { phase: 'idle' }
  | { phase: 'form' }
  | { phase: 'saving' }
  | { phase: 'landing' }
  | { phase: 'failed'; message: string; attempt: number };

export function ShippingAddressPicker({
  value,
  onChange,
  readOnly,
  customerId,
  customerName,
  stateProvinceId,
  cityTownId,
}: Props) {
  const addresses = useSalesOrderStore((s) => s.customerAddresses);
  const isLoading = useSalesOrderStore((s) => s.isLoadingAddresses);
  const appendCustomerAddress = useSalesOrderStore((s) => s.appendCustomerAddress);

  const [save, setSave] = useState<SaveState>({ phase: 'idle' });
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
  const [pickupIntent, setPickupIntent] = useState(false);

  const stateProvinceName = useReferenceName('stateProvinces', stateProvinceId);
  const cityTownName = useReferenceName('cityTowns', cityTownId);

  // A draft saved before this ticket can still carry a typed address — including the old
  // PICK-UP sentinel, which is no longer a valid shape. Both now surface as "legacy custom"
  // so the user re-picks; the submit validator rejects them too.
  const isLegacyCustom = !!value.custom;

  const pickupCandidates = useMemo(
    () => addresses.filter((a) => a.isPickupCandidate),
    [addresses],
  );
  // Exactly one → that is the customer's pick-up address. Two or more is genuinely ambiguous:
  // leave them in the saved list and let the user choose rather than guessing, and do NOT
  // offer to create (a duplicate could never be removed).
  const pickupAddress = pickupCandidates.length === 1 ? pickupCandidates[0] : null;
  const canCreatePickup = pickupCandidates.length === 0;

  const savedAddresses = useMemo(
    () => addresses.filter((a) => a.internalId !== pickupAddress?.internalId),
    [addresses, pickupAddress],
  );

  const selectedSaved = useMemo(
    () =>
      value.savedAddressId
        ? addresses.find((a) => a.internalId === value.savedAddressId)
        : undefined,
    [value.savedAddressId, addresses],
  );

  const isPickupSelected =
    !!pickupAddress && value.savedAddressId === pickupAddress.internalId;

  const selectValue = useMemo(() => {
    if (isPickupSelected) return PICKUP;
    if (pickupIntent && canCreatePickup) return PICKUP;
    if (value.savedAddressId) return value.savedAddressId;
    return '';
  }, [isPickupSelected, pickupIntent, canCreatePickup, value.savedAddressId]);

  const busy = save.phase === 'saving' || save.phase === 'landing';

  function selectAddress(id: string) {
    if (id === ADD_NEW) {
      setPickupIntent(false);
      setSave({ phase: 'form' });
      return;
    }
    if (id === PICKUP) {
      // Already has the record → selecting it is the whole interaction. Zero writes.
      if (pickupAddress) {
        setPickupIntent(false);
        select(pickupAddress);
        return;
      }
      // No record → show the one-click create confirm in the detail area. Deliberately NOT
      // the dialog: opening a form, even briefly, contradicts "nothing to fill in".
      setPickupIntent(true);
      return;
    }
    setPickupIntent(false);
    const addr = addresses.find((a) => a.internalId === id);
    if (addr) select(addr);
  }

  function select(addr: AddressDto) {
    setJustCreatedId((prev) => (prev === addr.internalId ? prev : null));
    onChange({ savedAddressId: addr.internalId, savedAddressText: addr.addrText, custom: null });
  }

  // The ONLY write path. Called from a button press — never from an effect or a submit path.
  async function runAppend(request: AppendAddressRequest, attempt: number) {
    if (!customerId) return;
    setSave({ phase: 'saving' });
    try {
      const newId = await appendCustomerAddress(customerId, request);
      // `landing` is the real address-list refetch inside appendCustomerAddress resolving —
      // by the time we get here the store already holds the new record.
      setSave({ phase: 'landing' });
      const fresh = useSalesOrderStore.getState().customerAddresses.find(
        (a) => a.internalId === newId,
      );
      setJustCreatedId(newId);
      setPickupIntent(false);
      onChange({
        savedAddressId: newId,
        savedAddressText: fresh?.addrText ?? request.addr1,
        custom: null,
      });
      setSave({ phase: 'idle' });
    } catch (err) {
      // Reopen with everything intact and show NetSuite's raw message.
      //
      // MUST go through getApiErrorMessage: axios REJECTS on 4xx, so the service's
      // `data.message` branch never runs and `err.message` is the useless
      // "Request failed with status code 400". The whole point of this block is that the
      // coordinator can forward NetSuite's actual complaint to whoever maintains that side.
      setSave({
        phase: 'failed',
        message: getApiErrorMessage(err, 'NetSuite rejected the address.'),
        attempt,
      });
    }
  }

  const attempt = save.phase === 'failed' ? save.attempt : 0;

  const control = (
    <AddressCombobox
      addresses={savedAddresses}
      selectedId={value.savedAddressId}
      pickupSelected={selectValue === PICKUP}
      justCreatedId={justCreatedId}
      disabled={isLoading || readOnly || busy}
      isLoading={isLoading}
      onSelect={selectAddress}
    />
  );

  const detail = busy ? (
    // Scoped skeleton, where the result will land — not a full-view spinner.
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[.04em] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {save.phase === 'saving' ? 'Saving to NetSuite' : 'Adding to the address book'}
      </div>
      <Skeleton className="h-[11px] w-[62%]" />
      <Skeleton className="h-[11px] w-[84%]" />
      <Skeleton className="h-[11px] w-[48%]" />
    </div>
  ) : pickupIntent && canCreatePickup ? (
    <div className="space-y-2 rounded-md border bg-muted/45 p-3">
      <div className="flex items-start gap-2">
        <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="space-y-1">
          <p className="text-[13px] font-medium">{customerName} has no PICK-UP address yet</p>
          <p className="text-xs text-muted-foreground">
            NetSuite needs an address record on the order, so pick-up uses a reusable one called
            PICK-UP. Creating it takes one click and happens <strong>once</strong> — every future
            pick-up order picks the same record.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          className="h-8 gap-1.5"
          disabled={!customerId || readOnly}
          onClick={() =>
            runAppend(
              {
                label: 'PICKUP',
                addressee: 'PICK-UP',
                addr1: 'PICK-UP',
                countryId: 'PH',
                provinceYn: PROVINCE_YN.NA,
              },
              1,
            )
          }
        >
          <CloudUpload className="h-3.5 w-3.5" />
          Create the PICK-UP address
        </Button>
        <Button variant="outline" className="h-8" onClick={() => setPickupIntent(false)}>
          Not now
        </Button>
      </div>
    </div>
  ) : isLegacyCustom ? (
    // AFTER the pick-up branch on purpose: this warns about a value the draft ARRIVED with, so a
    // fresh selection must be able to supersede it. Ordered first, choosing PICK-UP on a legacy
    // draft looked like it did nothing — the warning simply stayed put.
    <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div>
        This draft carries a typed address ({value.custom?.addressee || 'unnamed'}) — NetSuite no
        longer accepts them. Add the address here (it saves to the customer), then pick it from
        the list above.
      </div>
    </div>
  ) : isPickupSelected ? (
    <div className="space-y-2">
      {justCreatedId === pickupAddress?.internalId && (
        <Receipt
          title={`PICK-UP address created once · #${pickupAddress?.internalId}`}
          body={`${customerName} now has a reusable PICK-UP address. Every future pick-up order picks this one — nothing is created again.`}
        />
      )}
      <p className="text-sm text-muted-foreground">
        No delivery — the customer collects at the Capitol Steel plant. The order posts against{' '}
        {customerName}'s <strong>PICK-UP address</strong> (#{pickupAddress?.internalId}), so
        NetSuite has the address record it requires.
      </p>
      <ProvinceRow provinceYn={pickupAddress?.provinceYn ?? PROVINCE_YN.NA} />
    </div>
  ) : selectedSaved ? (
    <div className="space-y-2">
      {justCreatedId === selectedSaved.internalId && (
        <Receipt
          title={`Saved to NetSuite once · address #${selectedSaved.internalId}`}
          body={`It now lives on ${customerName}'s record and is selected for this order. Creating — or retrying — this Sales Order will not write it again.`}
        />
      )}
      <AddressPreview address={selectedSaved} />
      {selectedSaved.provinceYn && <ProvinceRow provinceYn={selectedSaved.provinceYn} />}
    </div>
  ) : addresses.length === 0 && !isLoading ? (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="space-y-1">
          <p className="text-[13px] font-medium">{customerName} has no saved addresses</p>
          <p className="text-xs text-muted-foreground">
            Nothing to pick yet. Add the first one here — it saves to their NetSuite record and is
            reusable on every future order.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          className="h-8"
          disabled={!customerId || readOnly}
          onClick={() => setSave({ phase: 'form' })}
        >
          Add the first address
        </Button>
        <Button
          variant="outline"
          className="h-8"
          disabled={!customerId || readOnly}
          onClick={() => setPickupIntent(true)}
        >
          It's a pick-up
        </Button>
      </div>
    </div>
  ) : (
    <span className="text-sm text-muted-foreground">No address selected</span>
  );

  return (
    <>
      <AddressCard icon={Truck} title="Shipping Address" required control={control} detail={detail} />
      {customerId && (
        <AddNewAddressDialog
          open={save.phase === 'form' || save.phase === 'failed'}
          customerId={customerId}
          customerName={customerName}
          orderStateProvinceName={stateProvinceName}
          orderCityTownName={cityTownName}
          failure={save.phase === 'failed' ? { message: save.message, attempt: save.attempt } : null}
          onSave={(req) => runAppend(req, attempt + 1)}
          onClose={() => setSave({ phase: 'idle' })}
        />
      )}
    </>
  );
}

/**
 * The dropdown — a searchable popover, not a flat Select (HANDOFF §2).
 *
 * Same idiom as SearchableSelect / CustomerCombobox (trigger button + Popover + filter Input +
 * `role="listbox"`), but written here because this list needs three things that shared component
 * has no notion of: grouping, per-row tags (DEFAULT / JUST ADDED), and a sticky action row that
 * stays reachable no matter what the filter matches.
 *
 * Filtering applies ONLY to the saved addresses. PICK-UP and "Add a new address" are answers to
 * "I have no address to pick", so a query that matches nothing is exactly when they matter most.
 */
function AddressCombobox({
  addresses,
  selectedId,
  pickupSelected,
  justCreatedId,
  disabled,
  isLoading,
  onSelect,
}: {
  addresses: AddressDto[];
  selectedId: string | null;
  pickupSelected: boolean;
  justCreatedId: string | null;
  disabled?: boolean;
  isLoading?: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return addresses;
    return addresses.filter((a) =>
      `${a.label ?? ''} ${a.addressee ?? ''} ${a.addrText ?? ''}`.toLowerCase().includes(q),
    );
  }, [addresses, query]);

  // Keyboard order matches visual order: PICK-UP, then the filtered saved rows.
  const navigable = useMemo(() => [PICKUP, ...filtered.map((a) => a.internalId)], [filtered]);

  const selectedLabel = useMemo(() => {
    if (pickupSelected) return 'PICK-UP';
    const hit = addresses.find((a) => a.internalId === selectedId);
    return hit
      ? hit.label || hit.addressee || hit.addrText?.slice(0, 60) || hit.internalId
      : '';
  }, [addresses, selectedId, pickupSelected]);

  function commit(id: string) {
    onSelect(id);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, navigable.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const id = navigable[activeIndex];
      if (id) commit(id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setQuery('');
        if (next) setActiveIndex(0);
      }}
    >
      <PopoverTrigger asChild>
        {/* Stable hook for the e2e suite: the card header renders "Shipping Address *" as one node
            and the billing column uses an identical control, so there is no unambiguous accessible
            name to select on. */}
        <button
          type="button"
          disabled={disabled}
          data-testid="shipping-address-select"
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !selectedLabel && 'text-muted-foreground',
          )}
        >
          <span className="truncate text-left">
            {isLoading ? 'Loading addresses…' : selectedLabel || 'Select an address…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search addresses…"
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>

        <ul
          role="listbox"
          aria-label="Shipping address"
          className="max-h-72 overflow-y-auto py-1"
          // Mirrors SearchableSelect: this popover can portal outside a Dialog whose scroll-lock
          // preventDefaults wheel events, which would leave the list unscrollable.
          onWheel={(e) => e.stopPropagation()}
        >
          <li className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">
            No address needed
          </li>
          <Row
            active={activeIndex === 0}
            selected={pickupSelected}
            onHover={() => setActiveIndex(0)}
            onClick={() => commit(PICKUP)}
            label="PICK-UP"
          />

          <li className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">
            Saved on this customer in NetSuite · {addresses.length}
          </li>
          {filtered.length === 0 ? (
            <li className="px-3 py-4 text-center text-xs text-muted-foreground">
              {addresses.length === 0 ? 'No saved addresses yet' : 'No matches'}
            </li>
          ) : (
            filtered.map((addr, idx) => (
              <Row
                key={addr.internalId}
                active={activeIndex === idx + 1}
                selected={addr.internalId === selectedId}
                onHover={() => setActiveIndex(idx + 1)}
                onClick={() => commit(addr.internalId)}
                label={
                  addr.label || addr.addressee || addr.addrText?.slice(0, 60) || addr.internalId
                }
                sub={addr.addrText?.split('\n').slice(1).join(', ') || undefined}
                tag={
                  addr.internalId === justCreatedId
                    ? 'JUST ADDED'
                    : addr.defaultShipping
                      ? 'DEFAULT'
                      : undefined
                }
              />
            ))
          )}
        </ul>

        {/* Sticky, outside the listbox: it is an action, not one of the options, and it must stay
            reachable when the filter matches nothing. */}
        <div className="border-t bg-muted/40">
          <button
            type="button"
            onClick={() => commit(ADD_NEW)}
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="flex flex-col">
              <span className="text-[13.5px] font-semibold">Add a new address</span>
              <span className="text-[11px] text-muted-foreground">
                Saved to the customer for next time
              </span>
            </span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({
  active,
  selected,
  onHover,
  onClick,
  label,
  sub,
  tag,
}: {
  active: boolean;
  selected: boolean;
  onHover: () => void;
  onClick: () => void;
  label: string;
  sub?: string;
  tag?: 'DEFAULT' | 'JUST ADDED';
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        onMouseEnter={onHover}
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
          active && 'bg-accent',
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{label}</span>
          {sub && <span className="truncate text-[11px] text-muted-foreground">{sub}</span>}
        </span>
        {tag && (
          <span
            className={cn(
              'shrink-0 rounded-full px-1.5 text-[10px] font-bold',
              tag === 'JUST ADDED'
                ? 'bg-success/[.12] text-success'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {tag}
          </span>
        )}
        {selected && <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />}
      </button>
    </li>
  );
}

function Receipt({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/[.08] p-2.5">
      <ShieldCheck className="mt-0.5 h-[15px] w-[15px] shrink-0 text-success" />
      <div className="space-y-0.5">
        <p className="text-[12.5px] font-semibold text-success">{title}</p>
        <p className="text-[11.5px] text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function ProvinceRow({ provinceYn }: { provinceYn: string }) {
  return (
    <p className="text-[11.5px] text-muted-foreground">
      Province (Y/N){' '}
      <span className="rounded bg-muted px-1.5 py-0.5 font-semibold text-foreground">
        {PROVINCE_YN_LABEL[provinceYn] ?? provinceYn}
      </span>{' '}
      · written with this address
    </p>
  );
}
